import React, { useEffect, useMemo } from "react";
import { tw } from "@careinfo/tailwind";
import { useForm } from "react-hook-form";
import Spreadsheet from "react-spreadsheet";
import Button from "../Reusable/Button";
import Card from "../Reusable/Card";
import CodeField from "../Reusable/CodeField";
import Modal from "../Reusable/Modal";
import TextField from "../Reusable/TextInput";
import { Group, Panel, Separator } from "react-resizable-panels";

const EditTemplate = (props: any) => {
  const {
    templateData,
    handleEdit,
    isPatchLoading,
    handleReset,
    isopenWarningPopup,
    setIsOpenWarningPopup,
  } = props;

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    control,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      slug: "",
      excel_layout: "",
    },
  });

  useEffect(() => {
    if (templateData) {
      const rawLayout = templateData?.excel_layout || templateData?.excelLayout || "";
      reset({
        name: templateData?.name || "",
        slug: templateData?.slug || "",
        excel_layout: typeof rawLayout === "string" ? rawLayout : JSON.stringify(rawLayout, null, 2),
      });
    }
  }, [templateData, reset]);

  const editorContent = watch("excel_layout");

  /**
   * This is the engine that converts your Template String into Spreadsheet Data.
   * It handles the conversion of keys like "A1" or "B4" into grid indices.
   */
  const spreadsheetData = useMemo(() => {
    if (!editorContent) return [];

    try {
      // 1. Clean the Django/Jinja syntax to make it valid JSON
      let cleaned = editorContent
        .replace(/{% autoescape off %}/g, "")
        .replace(/{% endautoescape %}/g, "")
        .replace(/{#.*?#}/g, "")             // Remove Comments
        .replace(/{%.*?%}/g, "")             // Remove Logic (for/if)
        .replace(/{{.*?}}/g, '"DUMMY"')      // Mock variables
        .replace(/,(\s*[\]}])/g, "$1")      // FIX: Remove illegal trailing commas
        .trim();

      const parsed = JSON.parse(cleaned);
      const rowsData = parsed.sheets?.[0]?.rows || [];
      
      // 2. Build a 40x20 Grid
      const grid: any[][] = Array.from({ length: 40 }, () =>
        Array.from({ length: 20 }, () => ({ value: "" }))
      );

      // 3. Map cell coordinates (e.g., A4 -> [3][0])
      rowsData.forEach((rowObj: any) => {
        const rowIndex = rowObj.row - 1; 
        if (rowIndex < 0 || rowIndex >= 40) return;

        Object.keys(rowObj.cells || {}).forEach((key) => {
          const colLetter = key.replace(/[0-9]/g, ""); 
          const colIndex = colLetter.charCodeAt(0) - 65; // A=0, B=1...
          
          if (colIndex >= 0 && colIndex < 20) {
            const cell = rowObj.cells[key];
            grid[rowIndex][colIndex] = {
              value: cell.value === "DUMMY" ? "Variable" : cell.value,
              readOnly: true,
              className: cell.style?.bold ? tw`font-bold bg-gray-50` : "",
            };
          }
        });
      });

      return grid;
    } catch (e) {
      // Log the error to console to see exactly where JSON.parse failed
      console.warn("JSON Cleaning failed. Raw string after cleaning was:", e);
      return null;
    }
  }, [editorContent]);

  return (
    <>
      <Card>
        <form
          onSubmit={handleSubmit((data: any) => handleEdit(data, setError))}
          autoComplete={"off"}
          className={tw`flex flex-col gap-y-6`}
        >
          <div className={tw`flex gap-x-4`}>
            <TextField register={register("name")} label={"Name"} errors={errors?.name} />
            <TextField register={register("slug")} label={"Slug"} errors={errors?.slug} />
          </div>

          <div>
            <label className={tw`block font-semibold text-gray-700 mb-2`}>
              Excel Layout Configuration
            </label>

            <Group orientation="horizontal" className={tw`h-[600px] border rounded-lg overflow-hidden shadow-sm`}>
              {/* CODE EDITOR SIDE */}
              <Panel defaultSize={45} minSize={30}>
                <CodeField control={control} name="excel_layout" language="json" />
              </Panel>

              <Separator className={tw`w-[2px] bg-gray-200 cursor-col-resize hover:bg-blue-400 transition-colors`} />

              {/* SPREADSHEET PREVIEW SIDE */}
              {/* <Panel defaultSize={55} minSize={30}>
                <div className={tw`h-full p-4 bg-gray-50 overflow-auto`}>
                  <div className={tw`flex justify-between items-center mb-4`}>
                    <p className={tw`text-[10px] font-bold text-gray-400 uppercase tracking-widest`}>
                      Visual Spreadsheet Preview
                    </p>
                    <span className={tw`bg-blue-100 text-blue-700 text-[9px] px-2 py-0.5 rounded font-bold uppercase`}>
                      Live View
                    </span>
                  </div>

                  {spreadsheetData ? (
                    <div className={tw`bg-white border rounded shadow-inner p-2 min-w-max`}>
                      <Spreadsheet data={spreadsheetData} />
                    </div>
                  ) : (
                    <div className={tw`flex flex-col items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded bg-white p-6 text-center`}>
                      <p className={tw`text-amber-600 font-semibold text-sm`}>Parsing Error Detected</p>
                      <p className={tw`text-gray-400 text-[11px] mt-2 max-w-xs`}>
                        Your JSON contains syntax that is currently breaking the preview. Check for missing quotes or brackets.
                      </p>
                    </div>
                  )}
                </div>
              </Panel> */}
            </Group>
          </div>

          <div className={tw`flex justify-end`}>
            <div className={tw`w-32`}>
              <Button label={"Update Template"} isLoading={isPatchLoading} type="submit" />
            </div>
          </div>
        </form>
      </Card>

      <Modal isOpen={isopenWarningPopup} setIsOpen={setIsOpenWarningPopup}>
        <div className="flex flex-col items-center p-6">
          <p className="font-semibold text-gray-800 text-lg">Reset to Default?</p>
          <div className={tw`flex gap-x-4 mt-8`}>
            <Button type="button" label={"Yes"} onClick={handleReset} />
            <Button
              type="button"
              label={"Cancel"}
              className={tw`bg-gray-100 text-black`}
              onClick={() => setIsOpenWarningPopup(false)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditTemplate;